import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilState } from 'recoil';

import {
  PhoneOrderLookupSection,
  prefillOfOrder,
} from '@service/seller/components/PhoneOrderLookupSection';
import {
  SellerCallOrderPrefill,
  sellerCallOrderPrefillAtom,
} from '@service/seller/state/sellerCallOrderPrefill.atom';
import PhraseInfoInputs from '@service/store/components/PhraseInfoInputs';
import { Button } from '@shared/components/Button';
import TelField from '@shared/components/TelField';
import { TextField } from '@shared/components/TextField';
import {
  Control,
  FieldValues,
  UseFormRegister,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';

//* shopItem checkout form

interface Props {
  register: UseFormRegister<FieldValues>;
  control: Control<FieldValues, any>;
  setValue: UseFormSetValue<FieldValues>;
  creatingCallOrder: boolean;
  //* 모달 폭을 함께 바꿔야 해서 열림 상태는 모달이 든다
  lookupOpen: boolean;
  setLookupOpen: (open: boolean) => void;
}
export const CreateCallOrderForm: FC<Props> = function CreateCallOrderForm({
  register,
  control,
  setValue,
  creatingCallOrder,
  lookupOpen,
  setLookupOpen,
}: Props) {
  //!만들어야할것
  // 1. 주문자정보
  // 2. 문구정보

  const [callOrderPrefill, setCallOrderPrefill] = useRecoilState(
    sellerCallOrderPrefillAtom,
  );
  const [appliedLabel, setAppliedLabel] = useState<string | null>(null);

  const telValue = useWatch({
    control,
    name: 'deliveryDetail.senderPhoneNumber',
    defaultValue: '',
  });

  //* 이전 주문 내용을 폼에 채운다. 부고 조회 페이지에서 담아온 값이든 여기서 검색한 값이든 같은 경로.
  const applyPrefill = useCallback(
    (prefill: SellerCallOrderPrefill) => {
      setValue('deliveryDetail.senderName', prefill.senderName);
      setValue('deliveryDetail.senderPhoneNumber', prefill.senderPhoneNumber);
      setValue('bugoDetail.senderPhrase', prefill.senderPhrase);
      setValue('bugoDetail.condolencePhrase', prefill.condolencePhrase);
      setAppliedLabel(prefill.sourceLabel);
      setLookupOpen(false);
    },
    [setLookupOpen, setValue],
  );

  //* 담아온 값은 이 단계 진입 시 한 번만 반영한다 —
  //* 1회 가드가 없으면 리렌더마다 사용자가 고친 값을 덮어쓴다.
  const didApplyPrefillRef = useRef(false);
  useEffect(() => {
    if (didApplyPrefillRef.current || !callOrderPrefill) return;
    didApplyPrefillRef.current = true;
    applyPrefill(callOrderPrefill);
    //* 다음 주문에 묻어나지 않게 담아둔 값은 비운다
    setCallOrderPrefill(null);
  }, [applyPrefill, callOrderPrefill, setCallOrderPrefill]);

  const formRender = useMemo(() => {
    return (
      //* 검색 패널이 열리면 넓은 화면에서 좌(입력)·우(검색결과) 2단으로 벌린다
      <div className={lookupOpen ? 'sm:flex sm:items-start sm:gap-5' : ''}>
        <div className={`space-y-4 text-left ${lookupOpen ? 'sm:w-1/2' : ''}`}>
          <div>
            <div className="flex items-center justify-between gap-2">
              <h4 className="font-bold">주문자 정보</h4>
              <Button
                className="button-sm whitespace-nowrap border border-gray-400 bg-white text-xs text-gray-700 hover:bg-gray-100"
                type="button"
                onClick={() => {
                  setLookupOpen(!lookupOpen);
                }}
              >
                {lookupOpen ? '검색 닫기' : '전화번호로 이전 주문 불러오기'}
              </Button>
            </div>

            {appliedLabel && (
              <p className="pt-2 text-xs text-cyan-700">
                이전 주문 내용을 불러왔습니다 · {appliedLabel}
              </p>
            )}

            <div className="grid grid-cols-2 grid-rows-2 gap-x-2 gap-y-3 pt-3">
              <div className="col-span-full">
                <TextField
                  className="w-full"
                  placeholder="성함"
                  {...register('deliveryDetail.senderName', { required: true })}
                />
              </div>
              <div className="col-span-full">
                <TelField
                  value={String(telValue)}
                  register={register}
                  name={'deliveryDetail.senderPhoneNumber'}
                  className="w-full"
                />
              </div>
            </div>
          </div>

          <PhraseInfoInputs
            register={register}
            control={control}
            names={{
              phraseSender: 'bugoDetail.senderPhrase',
              phraseCondolence: 'bugoDetail.condolencePhrase',
            }}
          />
        </div>

        {lookupOpen && (
          //* 좁은 칸이라 카드 1열 + 결과만 스크롤 (모달 전체가 늘어나 버튼이 화면 밖으로 나가지 않게)
          <div className="mt-4 rounded-lg border border-gray-200 p-3 text-left sm:mt-0 sm:w-1/2">
            <PhoneOrderLookupSection
              variant="panel"
              title="이전 주문 불러오기"
              description="(주문자와 문구가 왼쪽 폼에 바로 채워집니다)"
              selectLabel="이 내용으로 채우기"
              defaultPhoneNumber={String(telValue ?? '')}
              onSelect={(shopOrder) => {
                applyPrefill(prefillOfOrder(shopOrder));
              }}
            />
          </div>
        )}
      </div>
    );
  }, [
    appliedLabel,
    applyPrefill,
    control,
    lookupOpen,
    register,
    setLookupOpen,
    telValue,
  ]);

  return (
    <div>
      <div className="space-y-4 text-center">
        <p className="bugo-h1 text-18">정보 입력</p>
        {formRender}
        <div className="pt-4">
          <Button
            disabled={creatingCallOrder}
            className="button-rectangle text-base theme-text-3 theme-bg-main"
            type="submit"
          >
            주문생성
          </Button>
        </div>
      </div>
    </div>
  );
};
